import { defineAnalyticsEvent } from '@heidi/runtime/shell';

import type { PatientUpdatedEventProps } from '../contracts/patient-profile-update-analytics.types';
import type { PatientRecapSourceSessionClickedEventProps } from '../contracts/patient-recap-analytics.types';
import type { PatientSearchedAnalyticsProps } from '../contracts/patient-search-analytics.types';
import type {
  PatientDocumentLinkedEventProps,
  PatientDocumentUnlinkedEventProps,
  PatientDocumentUploadedEventProps,
  PatientFieldEditedEventProps,
  PatientListViewedEventProps,
  PatientProfileOpenedEventProps,
  PatientProfileTabViewedEventProps,
  PatientRecapPopoverOpenedEventProps,
  PatientSessionExpandedEventProps,
} from '../contracts/patient-surface-analytics.types';

/**
 * The twelve analytics events the patients domain owns outright, and the single
 * declaration site for each of their names (D9 ruling 27).
 *
 * FROZEN. Each `name` is the key a warehouse schema and its dashboards are built
 * on, so a rename is a new event and a second spelling silently splits a metric.
 * The `patient_profile_opened` → `patient_profile_tab_viewed` →
 * `patient_session_expanded` / `patient_field_edited` chain is one funnel
 * stitched by `view_id`; renaming any link makes the step-conversion rate divide
 * by a denominator that stopped being reported.
 *
 * `builds` is omitted throughout, i.e. `cloud-only` (D6). The on-prem appliance
 * ships without the SaaS analytics SDKs, so an event that reached the on-prem
 * registry would be a declaration nothing can emit.
 *
 * WHY TWELVE, MEASURED PER NAME OVER THE WHOLE TREE. Ownership is a property of
 * the ROOT SET, not of a file's directory, so each name below was scanned across
 * all of `src/**` and `packages/**` — for a `TrackingEventName.X` literal AND for
 * the `dispatch.analyticsScribe.trackX` wrapper, which hides the literal and is
 * why an enum-only scan under-reports. Ten names reach a patients root through a
 * literal; two reach one only through the wrapper (`patient_searched` from
 * `useSearchPatientProfiles.ts`, `patient_recap_source_session_clicked` from
 * `patient-recap-header.tsx`), which is why they are here despite reporting zero
 * literal sites.
 *
 * WHAT IS DELIBERATELY NOT HERE, in three groups:
 *
 * 1. **The cross-domain generics** — `button_clicked` (329 occurrences / 136
 *    files / 30 areas), `modal_viewed`, `screen_viewed` (26 / 23),
 *    `field_selected` (21 / 14). They belong to nobody: a name is globally
 *    unique and the second declarer is a `duplicate-event-name` collision. What
 *    patients owns of them is its `screen_name` / `action` / field-name
 *    vocabulary, which lives in `../contracts/*-analytics.types.ts` and is bound
 *    to the app enums by token maps the emit hosts assert.
 *
 * 2. **Patient-noun names with no patients-root emitter** — `patient_created`
 *    (only `src/hooks/mutation/useMutateCreatePatientProfile.ts`),
 *    `patient_summary_cta_shown` (only `src/container/ScribeTabs/**`), and
 *    `patient_linked` / `_selected` / `_unlinked` / `_viewed` /
 *    `_context_toggle_{on,off}` / `_extraction_*` / `_summary_*` (app-shell
 *    mutations, `src/components/ui/**`, or the uncarved `patientContext` root).
 *    An app-shell emitter is not a competing domain, but a name with NO
 *    patients-root emitter is not this domain's to declare in this row — so the
 *    measurement is recorded rather than the claim widened. Their payload types
 *    stay at the old path too, except the find-or-link funnel vocabulary, whose
 *    reasoning is in `../contracts/patient-find-or-link-analytics.types.ts`.
 *
 * 3. **The emit, and the routing.** A package declares its events and never
 *    imports a tracker (ruling 24) — the `track()` calls and the destination
 *    options stay app-side in
 *    `src/models/analytics/features/track*Analytics.ts`.
 *
 * `patient_updated` is the ONE of the twelve with emit sites outside the root
 * set; it is declared on the strength of its root emitter and the three-site
 * split is recorded in `../contracts/patient-profile-update-analytics.types.ts`.
 *
 * THE NINE SURFACE EVENTS' EMITTERS READ `descriptor.name` DIRECTLY, which is
 * new at this row and worth knowing before copying an earlier domain's host. The
 * three landed 2.1 domains all had to keep `TrackingEventName.X` at the emit
 * site, because `event_name` discriminates the app's `TrackingEventV2` union on
 * string ENUM members and a string literal does not compile in one's place. That
 * constraint applies to the BOUNDARY CROSSING, not to the whole path, so it now
 * lives once in the surface host's token map — and the nine call sites upstream
 * of it read `.name` off this file, which is what D9 ruling 27 asks for. The
 * other three still emit against the app union, so they keep the enum member and
 * their hosts' suites assert `String(TrackingEventName.X) === descriptor.name`.
 */

// ---------------------------------------------------------------------------
// The patient surface — /patients list, profile drawer, full-page profile, and
// the in-session affordances that reach them. These nine are exactly the members
// of `PatientSurfaceAnalyticsEvent`, and `analytics.test.ts` holds the two lists
// equal so a descriptor cannot be added without widening the seam.
// ---------------------------------------------------------------------------

export const patientListViewed = defineAnalyticsEvent<PatientListViewedEventProps>()({
  name: 'patient_list_viewed',
  description: 'The /patients list was viewed',
});

export const patientProfileOpened =
  defineAnalyticsEvent<PatientProfileOpenedEventProps>()({
    name: 'patient_profile_opened',
    description: 'A patient profile was opened in the drawer or the full page',
  });

export const patientProfileTabViewed =
  defineAnalyticsEvent<PatientProfileTabViewedEventProps>()({
    name: 'patient_profile_tab_viewed',
    description: 'A tab was activated inside an open patient profile view',
  });

export const patientSessionExpanded =
  defineAnalyticsEvent<PatientSessionExpandedEventProps>()({
    name: 'patient_session_expanded',
    description: "A session row was expanded in a patient profile's session list",
  });

export const patientFieldEdited = defineAnalyticsEvent<PatientFieldEditedEventProps>()({
  name: 'patient_field_edited',
  description: 'An inline patient-profile field edit was saved',
});

export const patientDocumentUploaded =
  defineAnalyticsEvent<PatientDocumentUploadedEventProps>()({
    name: 'patient_document_uploaded',
    description: 'A document finished uploading to a patient profile',
  });

export const patientDocumentLinked =
  defineAnalyticsEvent<PatientDocumentLinkedEventProps>()({
    name: 'patient_document_linked',
    description: "Patient documents were added to a session's context set",
  });

export const patientDocumentUnlinked =
  defineAnalyticsEvent<PatientDocumentUnlinkedEventProps>()({
    name: 'patient_document_unlinked',
    description: "Patient documents were removed from a session's context set",
  });

export const patientRecapPopoverOpened =
  defineAnalyticsEvent<PatientRecapPopoverOpenedEventProps>()({
    name: 'patient_recap_popover_opened',
    description: 'The in-session patient recap popover was opened',
  });

// ---------------------------------------------------------------------------
// The three that do not flow through the narrowed surface seam: two emit via the
// Rematch `analyticsScribe` wrapper and one via a bare `track()` against the app
// union. Their hosts keep the `TrackingEventName` member and assert it equals the
// `name` below.
// ---------------------------------------------------------------------------

export const patientUpdated = defineAnalyticsEvent<PatientUpdatedEventProps>()({
  name: 'patient_updated',
  description: 'Patient profile data was saved by an edit or an accepted extraction',
});

export const patientSearched = defineAnalyticsEvent<PatientSearchedAnalyticsProps>()({
  name: 'patient_searched',
  description: 'A patient-profile search returned a backend-confirmed result set',
});

export const patientRecapSourceSessionClicked =
  defineAnalyticsEvent<PatientRecapSourceSessionClickedEventProps>()({
    name: 'patient_recap_source_session_clicked',
    description: "The date link on a patient recap opened the recap's source session",
  });
